import React, { useState } from "react";
import Div from "@jumbo/shared/Div";
import JumboTextField from "@jumbo/components/JumboFormik/JumboTextField";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import Tab from "@mui/material/Tab";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Field } from "formik";
import { stringToBoolean } from "app/utils/appHelpers";
import AddIcon from "@mui/icons-material/Add";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import {
  TIME_BASED_PRICE_DISCOUNT_TYPES,
  TIME_BASED_PRICE_LABELS,
} from "app/utils/constants/settings";
import LoyaltyProgramSection from "./LoyaltyProgramSection";

const PricesTab = ({
  values,
  setFieldValue,
  t,
  vatRates,
  isGastro,
  loyaltyEnabled,
  loyaltyType,
  currencyCode,
}) => {
  const days = [
    {
      key: "monday",
      label: t("common.daysShort.monday", { defaultValue: "Mon" }),
    },
    {
      key: "tuesday",
      label: t("common.daysShort.tuesday", { defaultValue: "Tue" }),
    },
    {
      key: "wednesday",
      label: t("common.daysShort.wednesday", { defaultValue: "Wed" }),
    },
    {
      key: "thursday",
      label: t("common.daysShort.thursday", { defaultValue: "Thu" }),
    },
    {
      key: "friday",
      label: t("common.daysShort.friday", { defaultValue: "Fri" }),
    },
    {
      key: "saturday",
      label: t("common.daysShort.saturday", { defaultValue: "Sat" }),
    },
    {
      key: "sunday",
      label: t("common.daysShort.sunday", { defaultValue: "Sun" }),
    },
  ];
  const daysMenuProps = {
    PaperProps: {
      style: {
        maxHeight: 5 * 48, // show at least 5 items
      },
    },
  };
  const preventTyping = (e) => {
    e.preventDefault();
  };

  const isValid5MinTime = (time) => {
    if (typeof time !== "string") return true;
    const m = time.match(/^(\d{1,2}):(\d{2})$/);
    if (!m) return true; // keep existing validation behavior elsewhere
    const hh = Number(m[1]);
    const mm = Number(m[2]);
    if (!Number.isFinite(hh) || !Number.isFinite(mm)) return true;
    return mm % 5 === 0;
  };

  // Snap "HH:mm" to nearest 5-minute step (used only on user change, not on initial load)
  const snapTo5Minutes = (time) => {
    if (typeof time !== "string") return time;
    const m = time.match(/^(\d{1,2}):(\d{2})$/);
    if (!m) return time;
    let hh = Number(m[1]);
    let mm = Number(m[2]);
    if (!Number.isFinite(hh) || !Number.isFinite(mm)) return time;
    hh = Math.max(0, Math.min(23, hh));
    mm = Math.max(0, Math.min(59, mm));
    const total = hh * 60 + mm;
    let rounded = Math.round(total / 5) * 5;
    const max = 23 * 60 + 55;
    if (rounded > max) rounded = max;
    if (rounded < 0) rounded = 0;
    const outH = String(Math.floor(rounded / 60)).padStart(2, "0");
    const outM = String(rounded % 60).padStart(2, "0");
    return `${outH}:${outM}`;
  };
  const discountTypeOptions = Array.isArray(TIME_BASED_PRICE_DISCOUNT_TYPES)
    ? TIME_BASED_PRICE_DISCOUNT_TYPES.flat()
    : ["amount", "percent", "fixedPrice"];
  const labelOptions = Array.isArray(TIME_BASED_PRICE_LABELS)
    ? TIME_BASED_PRICE_LABELS.flat()
    : ["none", "sale", "happyHour"];

  const addTimePrice = () => {
    const next = Array.isArray(values.timeBasedPrices)
      ? [...values.timeBasedPrices]
      : [];
    next.push({
      days: [],
      timeFrom: "00:00",
      timeTo: "00:00",
      type: "amount",
      label: "none",
      value: 0,
    });
    setFieldValue("timeBasedPrices", next);
  };

  const updateTimePrice = (index, field, newValue) => {
    const next = Array.isArray(values.timeBasedPrices)
      ? [...values.timeBasedPrices]
      : [];
    if (!next[index]) return;
    next[index] = { ...next[index], [field]: newValue };
    setFieldValue("timeBasedPrices", next);
  };

  const removeTimePrice = (index) => {
    const next = Array.isArray(values.timeBasedPrices)
      ? [...values.timeBasedPrices]
      : [];
    next.splice(index, 1);
    setFieldValue("timeBasedPrices", next);
  };

  const [pricesSubTab, setPricesSubTab] = useState("vat");

  return (
    <Stack
      direction="row"
      width="100%"
      spacing={0}
      sx={{ alignItems: "stretch" }}
    >
      <TabContext value={pricesSubTab}>
        <Box
          sx={{
            borderRight: 1,
            borderColor: "divider",
            minWidth: 220,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <TabList
            orientation="vertical"
            onChange={(_, val) => setPricesSubTab(val)}
            sx={{
              flex: 1,
              "& .MuiTabList-flexContainer": {
                flexDirection: "column",
                alignItems: "stretch",
              },
            }}
          >
            <Tab
              label={t("pages.dsokProducts.pricesVat")}
              value="vat"
              sx={{ textTransform: "none", alignItems: "flex-start" }}
            />
            <Tab
              label={t("pages.dsokProducts.loyaltyProgram.title")}
              value="loyalty"
              sx={{ textTransform: "none", alignItems: "flex-start" }}
            />
            <Tab
              label={t("pages.dsokProducts.timeBasedPrices", {
                defaultValue: "Time Based Prices",
              })}
              value="timeBased"
              sx={{ textTransform: "none", alignItems: "flex-start" }}
            />
          </TabList>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, pl: 2 }}>
          <TabPanel value="vat" sx={{ p: 0, height: "100%" }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={12} lg={12}>
                <Typography variant="h4" color="gray" my={1}>
                  {t("pages.dsokProducts.pricesVat")}
                </Typography>
              </Grid>
              <Grid item xs={12} md={12} lg={12} sx={{ mb: 2 }}>
                <Alert severity="info" variant="outlined">
                  <AlertTitle>
                    {t("pages.dsokProducts.pricesInfo.title", {
                      defaultValue: "Ceny – ako to funguje",
                    })}
                  </AlertTitle>
                  <Typography variant="body2">
                    {t("pages.dsokProducts.pricesInfo.description", {
                      defaultValue:
                        "Základná cena a sadzba DPH sú povinné. Ak zapnete zľavu (prepínač Zľavnená cena), môžete zadať percento zľavy alebo priamo zľavnenú cenu – hodnoty sa navzájom prepočítavajú. Na kiosku sa zákazníkovi zobrazí zľavnená cena, ak je nastavená.",
                    })}
                  </Typography>
                </Alert>
              </Grid>
              <Grid container columnSpacing={2}>
                <Grid item md={4} xs={12} lg={2}>
                  <Div sx={{ mt: 2, mb: 1, width: "100%" }}>
                    <JumboTextField
                      type="number"
                      fullWidth
                      name="price"
                      value={values.price}
                      label={t("pages.products.price")}
                      InputLabelProps={{ required: true }}
                      InputProps={{
                        inputProps: {
                          min: 0,
                        },
                        onChange: (e) => {
                          const val = e.target.value;
                          setFieldValue("price", val);
                          const priceNum = Number(val);
                          const discount = Number(values.discountPercent) || 0;
                          const isSale =
                            values.isSalePrice &&
                            stringToBoolean(values.isSalePrice) === true;
                          if (isSale && val !== "" && !Number.isNaN(priceNum)) {
                            const computed = Math.max(
                              0,
                              Number(
                                (priceNum * (1 - discount / 100)).toFixed(2),
                              ),
                            );
                            setFieldValue("salePrice", computed);
                          }
                        },
                      }}
                    />
                  </Div>
                </Grid>
                <Grid item md={4} xs={12} lg={2}>
                  <Div sx={{ mt: 2, mb: 1, width: "100%" }}>
                    <JumboTextField
                      select
                      fullWidth
                      name="vatRate"
                      label={t("pages.dsokProducts.vatRate")}
                    >
                      {vatRates.map((rate) => {
                        return (
                          <MenuItem key={rate.key} value={rate.value}>
                            {rate.value}%
                          </MenuItem>
                        );
                      })}
                    </JumboTextField>
                  </Div>
                </Grid>
                <Grid item md={4} xs={12} lg={2}>
                  <Div sx={{ mt: 2, mb: 1, width: "100%" }}>
                    <FormControlLabel
                      control={
                        <Field
                          name={`isSalePrice`}
                          component={Switch}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setFieldValue(`isSalePrice`, checked);
                            if (!checked) return;
                            const priceNum = Number(values.price);
                            const currentDiscount = Number(
                              values.discountPercent,
                            );
                            const currentSale = Number(values.salePrice);
                            if (
                              !Number.isNaN(currentSale) &&
                              values.salePrice !== "" &&
                              priceNum > 0
                            ) {
                              const computedDiscount = Math.max(
                                0,
                                Math.min(
                                  100,
                                  Number(
                                    (
                                      (1 - currentSale / priceNum) *
                                      100
                                    ).toFixed(2),
                                  ),
                                ),
                              );
                              setFieldValue(
                                "discountPercent",
                                computedDiscount,
                              );
                            } else if (
                              !Number.isNaN(currentDiscount) &&
                              values.discountPercent !== "" &&
                              priceNum >= 0
                            ) {
                              const computedSale = Math.max(
                                0,
                                Number(
                                  (
                                    priceNum *
                                    (1 - currentDiscount / 100)
                                  ).toFixed(2),
                                ),
                              );
                              setFieldValue("salePrice", computedSale);
                            }
                          }}
                        />
                      }
                      label={t("pages.dsokProducts.isSalePrice")}
                      checked={stringToBoolean(values.isSalePrice)}
                    />
                  </Div>
                </Grid>
                {values.isSalePrice &&
                  stringToBoolean(values.isSalePrice) === true && (
                    <>
                      <Grid item md={4} xs={12} lg={2}>
                        <Div sx={{ mt: 2, mb: 1, width: "100%" }}>
                          <JumboTextField
                            type="number"
                            fullWidth
                            name="discountPercent"
                            value={values.discountPercent}
                            label={t("pages.dsokProducts.discountPercent")}
                            InputLabelProps={{ required: true }}
                            InputProps={{
                              inputProps: { min: 0 },
                              onChange: (e) => {
                                const val = e.target.value;
                                setFieldValue("discountPercent", val);
                                const discount = Number(val);
                                const priceNum = Number(values.price);
                                const isSale =
                                  values.isSalePrice &&
                                  stringToBoolean(values.isSalePrice) === true;
                                if (
                                  isSale &&
                                  !Number.isNaN(priceNum) &&
                                  val !== "" &&
                                  !Number.isNaN(discount)
                                ) {
                                  const computed = Math.max(
                                    0,
                                    Number(
                                      (priceNum * (1 - discount / 100)).toFixed(
                                        2,
                                      ),
                                    ),
                                  );
                                  setFieldValue("salePrice", computed);
                                }
                              },
                            }}
                          />
                        </Div>
                      </Grid>
                      <Grid item md={4} xs={12} lg={2}>
                        <Div sx={{ mt: 2, mb: 1, width: "100%" }}>
                          <JumboTextField
                            type="number"
                            fullWidth
                            name="salePrice"
                            value={values.salePrice}
                            label={t("pages.products.salePrice")}
                            InputLabelProps={{ required: false }}
                            InputProps={{
                              inputProps: { min: 0 },
                              onChange: (e) => {
                                const val = e.target.value;
                                setFieldValue("salePrice", val);
                                const isSale =
                                  values.isSalePrice &&
                                  stringToBoolean(values.isSalePrice) === true;
                                const saleNum = Number(val);
                                const priceNum = Number(values.price);
                                if (
                                  isSale &&
                                  val !== "" &&
                                  !Number.isNaN(saleNum) &&
                                  priceNum > 0
                                ) {
                                  const computedDiscount = Math.max(
                                    0,
                                    Math.min(
                                      100,
                                      Number(
                                        (
                                          (1 - saleNum / priceNum) *
                                          100
                                        ).toFixed(2),
                                      ),
                                    ),
                                  );
                                  setFieldValue(
                                    "discountPercent",
                                    computedDiscount,
                                  );
                                }
                              },
                            }}
                          />
                        </Div>
                      </Grid>
                    </>
                  )}
                <Grid item md={4} xs={12} lg={12}></Grid>
                {isGastro && (
                  <Grid item md={4} xs={12} lg={2}>
                    <Div sx={{ mt: 2, mb: 1, width: "100%" }}>
                      <FormControlLabel
                        control={
                          <Field
                            name={`isTakeaway`}
                            component={Switch}
                            onChange={(e) =>
                              setFieldValue(`isTakeaway`, e.target.checked)
                            }
                          />
                        }
                        label={t("pages.dsokProducts.isTakeaway")}
                        checked={stringToBoolean(values.isTakeaway)}
                      />
                    </Div>
                  </Grid>
                )}
                {isGastro &&
                  values.isTakeaway &&
                  stringToBoolean(values.isTakeaway) === true && (
                    <>
                      <Grid item md={4} xs={12} lg={2}>
                        <Div sx={{ mt: 2, mb: 1, width: "100%" }}>
                          <JumboTextField
                            select
                            fullWidth
                            name="vatRateTakeaway"
                            label={t("pages.dsokProducts.vatRateTakeaway")}
                          >
                            {vatRates.map((rate) => (
                              <MenuItem key={rate.key} value={rate.value}>
                                {rate.value}%
                              </MenuItem>
                            ))}
                          </JumboTextField>
                        </Div>
                      </Grid>
                      <Grid item md={4} xs={12} lg={2}>
                        <Div sx={{ mt: 2, mb: 1, width: "100%" }}>
                          <JumboTextField
                            type="number"
                            fullWidth
                            name="packagePrice"
                            value={values.packagePrice}
                            label={t("pages.dsokProducts.packagePrice")}
                            InputLabelProps={{ required: false }}
                            InputProps={{
                              inputProps: {
                                min: 0,
                              },
                              onChange: (e) =>
                                setFieldValue("packagePrice", e.target.value),
                            }}
                          />
                        </Div>
                      </Grid>
                    </>
                  )}
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value="loyalty" sx={{ p: 0, height: "100%" }}>
            <Grid container spacing={2}>
              {loyaltyEnabled === true &&
                loyaltyType &&
                loyaltyType !== "none" && (
                  <Grid item xs={12}>
                    <LoyaltyProgramSection
                      values={values}
                      setFieldValue={setFieldValue}
                      t={t}
                      currencyCode={currencyCode}
                    />
                  </Grid>
                )}
              {(!loyaltyEnabled || !loyaltyType || loyaltyType === "none") && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    {t("pages.dsokProducts.loyaltyProgram.notAvailable")}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </TabPanel>

          <TabPanel value="timeBased" sx={{ p: 0, height: "100%" }}>
            <Grid container spacing={2}>
              {/* Time Based Prices */}
              <Grid item xs={12} md={12} lg={12}>
                <Typography variant="h4" color="gray" my={1}>
                  {t("pages.dsokProducts.timeBasedPrices", {
                    defaultValue: "Time Based Prices",
                  })}
                </Typography>
              </Grid>

              {/* Info block */}
              <Grid item xs={12} md={12} lg={12} sx={{ mb: 2 }}>
                <Alert severity="info" variant="outlined">
                  <AlertTitle>
                    {t("pages.dsokProducts.timeBasedPricesInfo.title")}
                  </AlertTitle>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <b>{t("pages.dsokProducts.discountTypes.amount")}</b> –{" "}
                    {t("pages.dsokProducts.timeBasedPricesInfo.amount")}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <b>{t("pages.dsokProducts.discountTypes.percent")}</b> –{" "}
                    {t("pages.dsokProducts.timeBasedPricesInfo.percent")}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <b>{t("pages.dsokProducts.discountTypes.fixedPrice")} </b> –{" "}
                    {t("pages.dsokProducts.timeBasedPricesInfo.fixedPrice")}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {t("pages.dsokProducts.timeBasedPricesInfo.steps")}
                  </Typography>
                  <Typography variant="body2">
                    {t("pages.dsokProducts.timeBasedPricesInfo.evaluation")}
                  </Typography>
                </Alert>
              </Grid>

              <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
                <Grid item>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={addTimePrice}
                  >
                    {t("pages.dsokProducts.addTimePrice", {
                      defaultValue: "Add price",
                    })}
                  </Button>
                </Grid>
              </Grid>
              {Array.isArray(values.timeBasedPrices) &&
                values.timeBasedPrices.map((row, index) => (
                  <Grid
                    key={`tbp-${index}`}
                    container
                    columnSpacing={1}
                    alignItems="center"
                    sx={{ mb: 1 }}
                  >
                    <Grid item xs={12} md={4} lg={2}>
                      <FormControl fullWidth size="small">
                        <InputLabel>
                          {t("pages.dsokProducts.day", {
                            defaultValue: "Day(s)",
                          })}
                        </InputLabel>
                        <Select
                          multiple
                          label={t("pages.dsokProducts.day", {
                            defaultValue: "Day(s)",
                          })}
                          value={Array.isArray(row.days) ? row.days : []}
                          MenuProps={daysMenuProps}
                          renderValue={(selected) =>
                            (selected || [])
                              .map(
                                (key) =>
                                  days.find((d) => d.key === key)?.label || key,
                              )
                              .join(", ")
                          }
                          onChange={(e) =>
                            updateTimePrice(
                              index,
                              "days",
                              Array.isArray(e.target.value)
                                ? e.target.value
                                : [],
                            )
                          }
                        >
                          {days.map((d) => (
                            <MenuItem key={d.key} value={d.key}>
                              {d.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={1} lg={2}>
                      <TextField
                        type="time"
                        size="small"
                        fullWidth
                        label={t("pages.dsokProducts.timeFrom", {
                          defaultValue: "Time from",
                        })}
                        value={row.timeFrom || "00:00"}
                        onChange={(e) =>
                          updateTimePrice(
                            index,
                            "timeFrom",
                            snapTo5Minutes(e.target.value),
                          )
                        }
                        onKeyDown={preventTyping}
                        inputProps={{ step: 60 }}
                        InputLabelProps={{ shrink: true }}
                        error={!isValid5MinTime(row.timeFrom)}
                        helperText={
                          !isValid5MinTime(row.timeFrom)
                            ? t(
                                "pages.dsokProducts.timeBasedPricesValidation.step5",
                                {
                                  defaultValue:
                                    "Minutes must be in 5-minute steps.",
                                },
                              )
                            : ""
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={1} lg={2}>
                      <TextField
                        type="time"
                        size="small"
                        fullWidth
                        label={t("pages.dsokProducts.timeTo", {
                          defaultValue: "Time to",
                        })}
                        value={row.timeTo || "23:55"}
                        onChange={(e) =>
                          updateTimePrice(
                            index,
                            "timeTo",
                            snapTo5Minutes(e.target.value),
                          )
                        }
                        onKeyDown={preventTyping}
                        inputProps={{ step: 60 }}
                        InputLabelProps={{ shrink: true }}
                        error={!isValid5MinTime(row.timeTo)}
                        helperText={
                          !isValid5MinTime(row.timeTo)
                            ? t(
                                "pages.dsokProducts.timeBasedPricesValidation.step5",
                                {
                                  defaultValue:
                                    "Minutes must be in 5-minute steps.",
                                },
                              )
                            : ""
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={2} lg={2}>
                      <FormControl fullWidth size="small">
                        <InputLabel>
                          {t("pages.dsokProducts.discountType", {
                            defaultValue: "Discount Type",
                          })}
                        </InputLabel>
                        <Select
                          label={t("pages.dsokProducts.discountType", {
                            defaultValue: "Discount Type",
                          })}
                          value={row.type || "amount"}
                          onChange={(e) =>
                            updateTimePrice(index, "type", e.target.value)
                          }
                        >
                          {discountTypeOptions.map((code) => (
                            <MenuItem key={code} value={code}>
                              {t(`pages.dsokProducts.discountTypes.${code}`, {
                                defaultValue:
                                  code === "percent"
                                    ? "%"
                                    : code === "amount"
                                      ? "Amount"
                                      : code,
                              })}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2} lg={2}>
                      <FormControl fullWidth size="small">
                        <InputLabel>
                          {t("pages.dsokProducts.priceLabel", {
                            defaultValue: "Label",
                          })}
                        </InputLabel>
                        <Select
                          label={t("pages.dsokProducts.priceLabel", {
                            defaultValue: "Label",
                          })}
                          value={row.label || "none"}
                          onChange={(e) =>
                            updateTimePrice(index, "label", e.target.value)
                          }
                        >
                          {labelOptions.map((code) => (
                            <MenuItem key={code} value={code}>
                              {t(`pages.dsokProducts.priceLabels.${code}`, {
                                defaultValue:
                                  code === "sale"
                                    ? "Sale"
                                    : code === "happyHour"
                                      ? "Happy Hour"
                                      : "None",
                              })}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={1} lg={1}>
                      <TextField
                        type="number"
                        size="small"
                        fullWidth
                        label={t("pages.dsokProducts.value", {
                          defaultValue: "Value",
                        })}
                        inputProps={{ min: 0, step: 0.01 }}
                        value={row.value ?? 0}
                        onChange={(e) =>
                          updateTimePrice(
                            index,
                            "value",
                            Number(e.target.value),
                          )
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={1} lg={1}>
                      <IconButton
                        aria-label="delete-time-price"
                        onClick={() => removeTimePrice(index)}
                      >
                        <DeleteForeverIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                ))}
            </Grid>
          </TabPanel>
        </Box>
      </TabContext>
    </Stack>
  );
};

export default PricesTab;
